import React, { useCallback } from "react";
import { Box, IconButton, InputAdornment, TextField } from "@mui/material";
import { textUnit } from "../utils/PatternInvestigator";
import { RegexFinderWithDelete } from "./RegexFinderWithDelete";
import { RegexFinderWithSubtitution } from "./RegexFinderWithSubtitution";
import { RegexFinderWithTestSuit } from "./TestSuit/RegexFinderWithTestSuit";
import { ContentCopy } from "@mui/icons-material";
import { copyToClipboard } from "../utils/helpers";

export function TextStringGroup({
  textUnits: patternInvestigatorCollection,
  handleTextUnitsChange: handlePatternInvestigatorCollectionChange,
  currentTab,
  regex,
}: {
  textUnits: textUnit[];
  handleTextUnitsChange: (
    newPatternInvestigatorCollectionChange: textUnit[]
  ) => void;
  currentTab: string;
  regex: RegExp;
}) {
  const handleTextChange = (index: number, newText: string) => {
    const newRegexPatternFinder = patternInvestigatorCollection[index].clone();
    newRegexPatternFinder.text = newText;
    handlePatternInvestigatorChange(index, newRegexPatternFinder);
  };

  const handleDelete = (index: number) => {
    const newPatternInvestigatorCollectionChange =
      patternInvestigatorCollection.filter((_, i) => i !== index);
    handlePatternInvestigatorCollectionChange(
      newPatternInvestigatorCollectionChange
    );
  };

  const handleTextChangeCallback = useCallback(
    (index: number, newText: string) => {
      handleTextChange(index, newText);
    },
    [handleTextChange]
  );

  const handlePatternInvestigatorChange = (
    index: number,
    newPatternInvestigator: textUnit
  ) => {
    const newPatternInvestigatorCollectionChange = [
      ...patternInvestigatorCollection,
    ];
    newPatternInvestigatorCollectionChange[index] = newPatternInvestigator;
    handlePatternInvestigatorCollectionChange(
      newPatternInvestigatorCollectionChange
    );
  };

  const handleSubtitutionChange = (index: number, newSubtitution: string) => {
    const newRegexPatternFinder = patternInvestigatorCollection[index].clone();
    newRegexPatternFinder.subtitution = newSubtitution;
    handlePatternInvestigatorChange(index, newRegexPatternFinder);
  };

  const handleSubtitutionChangeCallback = useCallback(
    (index: number, newSubtitution: string) => {
      handleSubtitutionChange(index, newSubtitution);
    },
    [handleSubtitutionChange]
  );

  return (
    <>
      {patternInvestigatorCollection.map((regexPatternFinder, index) => (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            alignSelf: "stretch",
          }}
        >
          {currentTab === "MATCH" && (
            <Box>
              <RegexFinderWithDelete
                regex={regex}
                text={regexPatternFinder.text}
                handleTextChange={(newText: string) => {
                  handleTextChangeCallback(index, newText);
                }}
                handleDelete={() => {
                  handleDelete(index);
                }}
              />
            </Box>
          )}
          {currentTab === "SUBTITUTION" && (
            <RegexFinderWithSubtitution
              regex={regex}
              regexPatternFinder={regexPatternFinder}
              handleDelete={() => {
                handleDelete(index);
              }}
              handleTextChange={(newText) => {
                handleTextChangeCallback(index, newText);
              }}
              handleSubtitutionChange={(newSubtitution: string) => {
                handleSubtitutionChangeCallback(index, newSubtitution);
              }}
            />
          )}
          {currentTab === "UNIT TESTS" && (
            <RegexFinderWithTestSuit
              regex={regex}
              regexPatternFinder={regexPatternFinder}
              handleDelete={() => {
                handleDelete(index);
              }}
              handleTextChange={(newText) => {
                handleTextChangeCallback(index, newText);
              }}
            />
          )}
        </Box>
      ))}
    </>
  );
}

export function MatchingUnitsGroup({
  textUnits,
  handleTextUnitsChange,
}: {
  textUnits: textUnit[];
  handleTextUnitsChange: (
    newPatternInvestigatorCollectionChange: textUnit[]
  ) => void;
}) {
  const regex = /.+/g;
  const handleTextChange = (index: number, newText: string) => {
    const newRegexPatternFinder = textUnits[index].clone();
    newRegexPatternFinder.text = newText;
    handleTextUnitChange(index, newRegexPatternFinder);
  };

  const handleDelete = (index: number) => {
    const newTextUnitsChange = textUnits.filter((_, i) => i !== index);
    handleTextUnitsChange(newTextUnitsChange);
  };

  const handleTextChangeCallback = useCallback(
    (index: number, newText: string) => {
      handleTextChange(index, newText);
    },
    [handleTextChange]
  );

  const handleTextUnitChange = (index: number, newTextUnit: textUnit) => {
    const newTextUnitsChange = [...textUnits];
    newTextUnitsChange[index] = newTextUnit;
    handleTextUnitsChange(newTextUnitsChange);
  };

  return (
    <>
      {textUnits.map((regexPatternFinder, index) => (
        <RegexFinderWithDelete
          regex={regex}
          text={regexPatternFinder.text}
          handleTextChange={(newText: string) => {
            handleTextChangeCallback(index, newText);
          }}
          handleDelete={() => {
            handleDelete(index);
          }}
        />
      ))}
    </>
  );
}

export function SubtitutionUnitsGroup({
  textUnits,
  handleTextUnitsChange,
}: {
  textUnits: textUnit[];
  handleTextUnitsChange: (
    newPatternInvestigatorCollectionChange: textUnit[]
  ) => void;
}) {
  const regex = /.+/g;
  const handleTextChange = (index: number, newText: string) => {
    const newRegexPatternFinder = textUnits[index].clone();
    newRegexPatternFinder.text = newText;
    handleTextUnitChange(index, newRegexPatternFinder);
  };

  const handleDelete = (index: number) => {
    const newTextUnitsChange = textUnits.filter((_, i) => i !== index);
    handleTextUnitsChange(newTextUnitsChange);
  };

  const handleTextChangeCallback = useCallback(
    (index: number, newText: string) => {
      handleTextChange(index, newText);
    },
    [handleTextChange]
  );

  const handleTextUnitChange = (index: number, newTextUnit: textUnit) => {
    const newTextUnitsChange = [...textUnits];
    newTextUnitsChange[index] = newTextUnit;
    handleTextUnitsChange(newTextUnitsChange);
  };

  return (
    <>
      {textUnits.map((regexPatternFinder, index) => (
        <Box
          style={{
            display: "flex",
            padding: "var(--2, 16px) var(--none, 0px)",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "var(--4, 16px)",
            alignSelf: "stretch",
            borderBottom: "2px solid black",
          }}
        >
          <RegexFinderWithDelete
            regex={regex}
            text={regexPatternFinder.text}
            handleTextChange={(newText: string) => {
              // handleTextChange(newText);
            }}
            handleDelete={() => {
              // handleTextChange(newText);
            }}
          />

          <TextField
            label="SUBTITUTION"
            variant="standard"
            style={{
              alignSelf: "stretch",
            }}
            value={regexPatternFinder.subtitution}
            onChange={(e) => {
              // handleSubtitutionChange(e.target.value);
            }}
          />

          <TextField
            label="OUTPUT"
            variant="standard"
            disabled
            style={{
              padding: "var(--none, 0px)",
              gap: "var(--none, 0px)",
              flex: "1 0 0",
              alignSelf: "stretch",
            }}
            multiline
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      copyToClipboard(
                        regexPatternFinder.getSubtitutionOutput()
                      );
                    }}
                  >
                    <ContentCopy />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            value={regexPatternFinder.getSubtitutionOutput()}
          />
        </Box>
      ))}
    </>
  );
}
